import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Heart, Share2, RefreshCw, ShoppingBag, ArrowLeft, Globe } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { api, fileUrl, formatError, downloadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MerchDialog } from "@/components/MerchDialog";
import { ShareButtons } from "@/components/ShareButtons";

const PUNS = ["Pawsing for magic...", "Combing whiskers...", "Applying royal glitter...", "Fetching the perfect lighting...", "Negotiating with the treat budget...", "Adding extra floof..."];

const Loading = ({ gen }) => {
  const [pct, setPct] = useState(4);
  const [i, setI] = useState(0);
  useEffect(() => {
    const a = setInterval(() => setPct((p) => Math.min(95, p + Math.random() * 6)), 900);
    const b = setInterval(() => setI((x) => (x + 1) % PUNS.length), 2400);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);
  return (
    <div className="rounded-[2rem] bg-white sticker p-6 sm:p-10" data-testid="generation-loading">
      <div className="relative aspect-square max-w-md mx-auto rounded-3xl overflow-hidden">
        <img src={fileUrl(gen.input_path)} alt="" className="h-full w-full object-cover blur-sm scale-110 opacity-70" />
        <div className="absolute inset-0 shimmer opacity-60 mix-blend-overlay" />
      </div>
      <p className="mt-8 text-center font-display text-2xl font-semibold" data-testid="generation-loading-pun">{PUNS[i]}</p>
      <Progress value={pct} className="mt-4 h-3 max-w-md mx-auto" />
      <p className="mt-3 text-center text-sm text-[#6E687A]">Usually 15-40 seconds. Don't leave, the magic is happening.</p>
    </div>
  );
};

const Action = ({ icon: Icon, label, onClick, testid, active }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center justify-center gap-2 rounded-full py-3 px-5 font-bold text-sm sticker sticker-press ${active ? "bg-[#FF5A79] text-white" : "bg-white"}`}>
    <Icon className={`h-4 w-4 ${active ? "fill-current" : ""}`} /> {label}
  </button>
);

export default function Result() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [gen, setGen] = useState(null);
  const [showBefore, setShowBefore] = useState(false);
  const [merch, setMerch] = useState(false);

  useEffect(() => {
    let t;
    const load = async () => {
      try {
        const { data } = await api.get(`/generations/${id}`);
        setGen(data);
        if (data.status === "processing") t = setTimeout(load, 2500);
        else if (data.status === "failed") {
          toast.error(data.error_code === "ai_unavailable" ? "AI studio is temporarily unavailable. Credit refunded." : "That one didn't work. Your credit was refunded.");
          refresh();
        }
      } catch (err) { toast.error(formatError(err)); }
    };
    load();
    return () => clearTimeout(t);
  }, [id, refresh]);

  if (!gen) return <div className="max-w-3xl mx-auto px-4 py-16"><div className="shimmer aspect-square rounded-3xl" /></div>;

  const fav = async () => { const { data } = await api.post(`/generations/${id}/favorite`); setGen({ ...gen, is_favorite: data.is_favorite }); toast.success(data.is_favorite ? "Added to favorites" : "Removed from favorites"); };
  const share = async () => { const { data } = await api.post(`/generations/${id}/share`); setGen({ ...gen, is_public: data.is_public }); toast.success(data.is_public ? "Posted to the community feed!" : "Removed from community"); };
  const regen = async () => {
    try { const { data } = await api.post("/generations", { pet_id: gen.pet_id, prompt_id: gen.prompt_id }); refresh(); nav(`/creation/${data.generation_id}`); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="result-page">
      <Link to="/studio" className="inline-flex items-center gap-1 text-sm font-semibold text-[#6E687A] hover:text-[#1E1B26]" data-testid="result-back-link"><ArrowLeft className="h-4 w-4" /> Back to studio</Link>
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">{gen.pet_name} as <span className="text-[#FF5A79]">{gen.prompt_title}</span></h1>

      <div className="mt-8">
        {gen.status === "processing" && <Loading gen={gen} />}
        {gen.status === "failed" && (
          <div className="rounded-3xl bg-white border border-[#EAE3D9] p-10 text-center" data-testid="generation-failed">
            {gen.error_code === "ai_unavailable" ? (
              <>
                <p className="font-display text-2xl font-semibold" data-testid="generation-failed-title">Our AI artists are on a quick nap.</p>
                <p className="mt-2 text-[#6E687A]" data-testid="generation-failed-message">The AI studio is temporarily unavailable. Your credit was refunded, so please try again in a little while.</p>
              </>
            ) : (
              <>
                <p className="font-display text-2xl font-semibold" data-testid="generation-failed-title">The AI dropped its paintbrush.</p>
                <p className="mt-2 text-[#6E687A]" data-testid="generation-failed-message">Your credit was refunded. Try again or pick another style.</p>
              </>
            )}
            <div className="mt-6 flex justify-center gap-3"><Action icon={RefreshCw} label="Try again" onClick={regen} testid="result-retry-button" /></div>
          </div>
        )}
        {gen.status === "completed" && (
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="rounded-[2rem] bg-white sticker p-3 rise">
                <img src={fileUrl(showBefore ? gen.input_path : gen.output_path)} alt="creation" className="w-full rounded-3xl aspect-square object-cover" data-testid="result-image" />
              </div>
              <div className="mt-5 inline-flex rounded-full bg-white border border-[#EAE3D9] p-1" data-testid="before-after-toggle">
                {["After", "Before"].map((l) => (
                  <button key={l} onClick={() => setShowBefore(l === "Before")} data-testid={`toggle-${l.toLowerCase()}`}
                    className={`px-5 py-1.5 rounded-full text-sm font-bold transition-colors ${(l === "Before") === showBefore ? "bg-[#1E1B26] text-white" : ""}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Action icon={Download} label="Download" testid="result-download-button" onClick={() => downloadImage(gen.output_path, `${gen.pet_name}-${gen.prompt_id}.png`).then(() => toast.success("Downloaded!")).catch(() => toast.error("Download failed, please try again"))} />
                <Action icon={Heart} label={gen.is_favorite ? "Favorited" : "Favorite"} active={gen.is_favorite} onClick={fav} testid="result-favorite-button" />
                <Action icon={Globe} label={gen.is_public ? "Shared" : "Share to feed"} active={gen.is_public} onClick={share} testid="result-share-button" />
                <Action icon={RefreshCw} label="Regenerate" onClick={regen} testid="result-regenerate-button" />
              </div>
              {gen.is_public && <ShareButtons id={id} text={`Meet ${gen.pet_name} as ${gen.prompt_title}! Made with Cutie Parlor`} />}
              {gen.is_public && <Link to={`/c/${id}`} className="block text-center text-sm font-bold text-[#8A4FFF]" data-testid="result-public-page-link">Open public page</Link>}
              <div className="rounded-3xl bg-[#8A4FFF] text-white p-6 sticker">
                <ShoppingBag className="h-7 w-7 text-[#FFC72C]" />
                <h3 className="mt-3 text-xl font-semibold">Make it merch</h3>
                <p className="mt-1 text-sm text-white/80">Mugs, tees and posters printed on demand.</p>
                <button onClick={() => setMerch(true)} data-testid="result-order-merch-button" className="mt-4 rounded-full bg-[#FFC72C] text-[#1E1B26] px-5 py-2.5 font-bold text-sm sticker sticker-press">Order a product</button>
              </div>
              <Link to="/gallery" className="block text-center text-sm font-semibold text-[#6E687A] hover:text-[#1E1B26]" data-testid="result-gallery-link"><Share2 className="inline h-4 w-4 mr-1" /> View all my creations</Link>
            </div>
          </div>
        )}
      </div>
      {gen.status === "completed" && <MerchDialog open={merch} onOpenChange={setMerch} generation={gen} />}
    </main>
  );
}
